import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Lock, LayoutDashboard, ShieldCheck, Rocket, Phone, Menu, X } from "lucide-react";
import { WeCockpitLogo } from "./WeCockpitLogo";

/**
 * PublicHeader — Header partagé entre les pages publiques (Home, Fonctionnalités,
 * Sites réalisés, Onboarding, Features). Garde une seule source de vérité pour
 * la navigation principale + le bouton de connexion.
 *
 * Props :
 *   tone       : "light" | "dark" — couleur de fond (light=fond clair, dark=fond navy)
 *   agencyName : nom de l'agence pour l'attribution
 *   compact    : si true, masque la tagline secondaire
 */
export default function PublicHeader({
  tone = "dark",
  agencyName = "Agence MSC",
  compact = false,
}) {
  const isDark = tone === "dark";
  const [mobileOpen, setMobileOpen] = useState(false);
  const linkClass = isDark
    ? "hover:text-white text-sky-200 transition"
    : "hover:text-slate-900 text-slate-500 transition";
  const contactClass = isDark
    ? "hover:text-white text-white transition"
    : "hover:text-slate-900 text-slate-800 transition";
  const sepClass = isDark ? "text-sky-700/40" : "text-slate-300";
  const dotBtnClass = isDark
    ? "bg-white/10 hover:bg-white/20 border-white/20 hover:border-white/40 text-white"
    : "bg-slate-900/5 hover:bg-slate-900/10 border-slate-200 text-slate-700";
  const mobileMenuBtnClass = isDark
    ? "bg-white/10 hover:bg-white/20 border-white/20 text-white"
    : "bg-slate-900/5 hover:bg-slate-900/10 border-slate-200 text-slate-800";
  const mobilePanelBg = isDark ? "bg-[#0F2744] border-white/10" : "bg-white border-slate-200";
  const mobileLinkColor = isDark ? "text-sky-100 hover:bg-white/10" : "text-slate-700 hover:bg-slate-50";

  // Coordonnées WECOCKPIT — centralisées
  const PHONE = "04 84 49 27 00";
  const PHONE_TEL = "+33484492700";

  // Close mobile menu on route change / escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <header className="relative max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-8 pb-3 md:pb-4" data-testid="public-header">
      {/* Desktop — logo gauche / nav droite */}
      <div className="hidden md:flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-4 group">
          <WeCockpitLogo variant="lockup" size={42} tone={tone} data-testid="public-header-logo"/>
          {!compact && (
            <div className={`border-l ${isDark ? "border-white/15" : "border-slate-200"} pl-4 ml-1 leading-tight`}>
              <div className={`text-[9px] uppercase tracking-[0.22em] font-bold ${isDark ? "text-sky-300/90" : "text-sky-600/80"}`}>Pilotons ensemble</div>
              <div className={`text-[12px] font-semibold -mt-0.5 ${isDark ? "text-white" : "text-slate-700"}`}>votre présence digitale</div>
            </div>
          )}
        </Link>

        <nav className="flex items-center gap-3 text-xs" data-testid="public-header-nav">
          <Link to="/cockpit-features" className={linkClass} data-testid="nav-features">Fonctionnalités</Link>
          <span className={sepClass}>|</span>
          <Link to="/cockpit-features#widgets" className={linkClass} data-testid="nav-widgets">Widgets</Link>
          <span className={sepClass}>|</span>
          <a href={`tel:${PHONE_TEL}`} className={`${contactClass} inline-flex items-center gap-1 font-bold`} data-testid="nav-phone">
            <Phone className="w-3 h-3"/> {PHONE}
          </a>
          <span className="ml-2">
            <ConnexionDot agencyName={agencyName} tone={tone} dotBtnClass={dotBtnClass}/>
          </span>
        </nav>
      </div>

      {/* Mobile — vrai header horizontal : logo gauche / actions droite */}
      <div className="flex md:hidden items-center justify-between gap-2" data-testid="public-header-mobile">
        <Link to="/" className="flex items-center shrink-0" onClick={() => setMobileOpen(false)}>
          <WeCockpitLogo variant="lockup" size={34} tone={tone}/>
        </Link>
        <div className="flex items-center gap-1.5">
          <a
            href={`tel:${PHONE_TEL}`}
            className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition ${dotBtnClass}`}
            data-testid="mobile-nav-phone"
            aria-label={`Appeler ${PHONE}`}
          >
            <Phone className="w-3.5 h-3.5"/>
          </a>
          <ConnexionDot agencyName={agencyName} tone={tone} dotBtnClass={dotBtnClass}/>
          <button
            type="button"
            onClick={() => setMobileOpen(o => !o)}
            className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition ${mobileMenuBtnClass}`}
            aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-4 h-4"/> : <Menu className="w-4 h-4"/>}
          </button>
        </div>
      </div>

      {/* Mobile — panneau dépliant */}
      {mobileOpen && (
        <>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            aria-label="Fermer le menu"
          />
          <div
            className={`md:hidden absolute left-4 right-4 top-full mt-2 z-50 rounded-2xl border shadow-2xl overflow-hidden ${mobilePanelBg}`}
            data-testid="mobile-menu-panel"
          >
            <Link
              to="/cockpit-features"
              onClick={() => setMobileOpen(false)}
              className={`block px-5 py-3.5 text-sm font-semibold ${mobileLinkColor} border-b ${isDark ? "border-white/5" : "border-slate-100"}`}
              data-testid="mobile-nav-features"
            >
              Fonctionnalités
            </Link>
            <Link
              to="/cockpit-features#widgets"
              onClick={() => setMobileOpen(false)}
              className={`block px-5 py-3.5 text-sm font-semibold ${mobileLinkColor} border-b ${isDark ? "border-white/5" : "border-slate-100"}`}
              data-testid="mobile-nav-widgets"
            >
              Widgets
            </Link>
            <a
              href={`tel:${PHONE_TEL}`}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-bold ${mobileLinkColor}`}
            >
              <Phone className="w-4 h-4"/> {PHONE}
            </a>
          </div>
        </>
      )}
    </header>
  );
}

/**
 * ConnexionDot — petit bouton rond cadenas avec menu dropdown
 * (tableau de bord site / espace agence / créer un compte).
 */
function ConnexionDot({ agencyName, tone, dotBtnClass }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (!e.target.closest("[data-connexion-menu]")) setOpen(false); };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  return (
    <div className="relative inline-block" data-connexion-menu data-testid="header-connexion-menu">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition ${dotBtnClass}`}
        data-testid="header-connexion-dot"
        aria-label="Connexion ou créer un compte"
        title="Connexion / Créer un compte"
      >
        <Lock className="w-3.5 h-3.5"/>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-slate-200 shadow-2xl shadow-slate-900/30 overflow-hidden z-50">
          <Link to="/admin/login" onClick={() => setOpen(false)} className="flex items-start gap-3 p-3.5 hover:bg-sky-50 transition border-b border-slate-100" data-testid="header-login-site">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-500 text-white flex items-center justify-center shrink-0 shadow-sm">
              <LayoutDashboard className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">Back-office d'un site</div>
              <div className="font-bold text-sm text-slate-900">Tableau de bord du site</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Avec les identifiants client (Botanica, Kloubair…)</div>
            </div>
          </Link>
          <Link to="/mon-espace/login" onClick={() => setOpen(false)} className="flex items-start gap-3 p-3.5 hover:bg-violet-50 transition border-b border-slate-100" data-testid="header-login-customer-space">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shrink-0 shadow-sm">
              <Rocket className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-violet-700 font-bold">Espace client WECOCKPIT</div>
              <div className="font-bold text-sm text-slate-900">Mon espace client</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Sites, devis, projets, contacts — pour les entreprises clientes.</div>
            </div>
          </Link>
          <Link to="/agency/login?force=1" onClick={() => setOpen(false)} className="flex items-start gap-3 p-3.5 hover:bg-emerald-50 transition" data-testid="header-login-agency">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center shrink-0 shadow-sm">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Plateforme WECOCKPIT</div>
              <div className="font-bold text-sm text-slate-900">Espace {agencyName}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Pilotage global · réservé à l'équipe agence</div>
            </div>
          </Link>
        </div>
      )}
    </div>
  );
}
